import type { ObjectMetadata } from '@olytic/foundation';

import { formatMoney } from '../../objects/format';
import { OBJECT_DEFS } from '../../objects/objectDefs';
import { asCheck, asDate, asDateTime, asLabel, column, createdByField, field, lastModifiedByField, lookup, type Row, related, show, text } from './kit';

const def = OBJECT_DEFS['billing-accounts'];
const money = show(formatMoney);

/** Who an invoice goes to: the bill-to. Rationale: billing-accounts.md. */
export const billingAccounts: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Billing account',
  plural: def.label,
  icon: def.icon,
  listColumns: def.columns,
  details: [
    field('name', 'Name'),
    field('account_name', 'Account', { link: lookup('accounts', 'account_id') }),
    field('is_default', 'Default', { format: asCheck }),
    field('payment_terms', 'Payment Terms', { format: asLabel }),
    field('invoice_contact_name', 'Invoice Contact', { link: lookup('contacts', 'invoice_contact_id') }),
    field('invoice_email', 'Invoice Email'),
    field('created_at', 'Created', { format: asDateTime }),
    field('updated_at', 'Last Modified', { format: asDateTime }),
    field('qbo_customer_id', 'QuickBooks Customer ID'),
    createdByField(),
    lastModifiedByField(),
  ],
  sections: [
    { key: 'identity', fields: ['name', 'account_name', 'is_default', 'payment_terms', 'invoice_contact_name', 'invoice_email'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'updated_at', 'last_modified_by_name', 'qbo_customer_id'] },
  ],
  related: [
    related('invoices', 'Invoices', 'invoices', [
      column('name', 'Invoice', { primary: true }),
      column('invoice_date', 'Date', { format: asDate }),
      column('status', 'Status', { format: asLabel }),
      column('total', 'Total', { format: money, align: 'right' }),
    ]),
    related('billing-schedules', 'Billing Schedule', 'billing-schedules', [
      column('name', 'Billing Row', { primary: true }),
      column('planned_date', 'Invoice Date', { format: asDate }),
      column('amount', 'Amount', { format: money, align: 'right' }),
      column('status', 'Status', { format: asLabel }),
    ]),
  ],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'account_name', 'invoice_email'],
};
